import { useCallback, useEffect } from 'react';
import useUserStore from 'common/src/stores/useUserStore';
import {
  getMyShipmentCases,
  SHIPMENT_CASES_CHANGED_EVENT,
} from 'common/src/api/shipment-cases';
import { useScopedRequest } from '../pages/shipments/arrival-manager/use-scoped-request';

export default function useMyShipmentCaseCount() {
  const { userProfile } = useUserStore();
  const exporterId = userProfile?.exporter_id;
  const permissions = userProfile?.permissions || [];
  const allowed =
    userProfile?.roles.some((role) => ['admin', 'superadmin'].includes(role)) ||
    ['claims', 'shipments-details'].every((key) =>
      permissions.some((p) => p.sidebarItemKey === key && p.enabled)
    );
  const key =
    allowed && exporterId && userProfile?.auth_id
      ? `${userProfile.auth_id}:${userProfile.id}:${exporterId}:my-case-count`
      : null;
  const load = useCallback(
    async (signal: AbortSignal) =>
      (await getMyShipmentCases(exporterId!, signal)).length,
    [exporterId]
  );
  const request = useScopedRequest(key, load);
  const reload = request.reload;
  useEffect(() => {
    if (!key) return;
    const update = () => {
      if (!document.hidden) void reload();
    };
    const invalidate = (event: Event) => {
      if (
        (event as CustomEvent<{ exporterId: number }>).detail?.exporterId ===
        exporterId
      )
        void reload();
    };
    const timer = window.setInterval(update, 30_000);
    window.addEventListener('focus', update);
    document.addEventListener('visibilitychange', update);
    window.addEventListener(SHIPMENT_CASES_CHANGED_EVENT, invalidate);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', update);
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener(SHIPMENT_CASES_CHANGED_EVENT, invalidate);
    };
  }, [key, exporterId, reload]);
  return key ? (request.data ?? null) : undefined;
}
